/**
 * GuidePage — Field Guide root.
 *
 * Two render modes:
 *   1. `/guide`                 → index of all sections (mode: 'root')
 *   2. `/guide/<section>`       → index of articles in one section (mode: 'section')
 *
 * Article view (`/guide/<section>/<article>`) lives in GuideArticlePage.
 */

import { useEffect, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDocumentLang } from '@/hooks/useDocumentLang';
import { useJsonLd, useSeoMeta } from '@/hooks/useSeoMeta';
import {
  SECTIONS,
  articlesForSection,
  buildGuideUrl,
  getSectionDef,
  isSectionSlug,
  type LangCode,
  type SectionSlug,
} from '@/features/guide';
import { useGuideHreflang } from '@/features/guide';

interface GuidePageProps {
  lang: LangCode;
}

export default function GuidePage({ lang }: GuidePageProps) {
  useDocumentLang();
  const { t, i18n } = useTranslation();
  const { section: sectionParam } = useParams<{ section?: string }>();

  useEffect(() => {
    if (i18n.language !== lang) {
      i18n.changeLanguage(lang).catch(() => {});
    }
  }, [lang, i18n]);

  const sectionSlug = useMemo<SectionSlug | null>(
    () => (isSectionSlug(sectionParam) ? (sectionParam as SectionSlug) : null),
    [sectionParam],
  );

  const mode: 'root' | 'section' | 'not-found' =
    !sectionParam ? 'root' : sectionSlug ? 'section' : 'not-found';

  const guideLabel = t('guide.label', { defaultValue: 'Field Guide' });

  // SEO
  const rootTitle = `${guideLabel} · Lodestar`;
  const rootDescription = t('guide.metaDescription', {
    defaultValue:
      'Lodestar Field Guide — how to use the map, pins, shortlist, and compare table to find a place to live.',
  });
  const sectionLabel = sectionSlug
    ? t(`guide.section.${getSectionDef(sectionSlug).i18nKey}.label`, {
        defaultValue: getSectionDef(sectionSlug).i18nKey,
      })
    : '';
  const sectionDescription = sectionSlug
    ? t(`guide.section.${getSectionDef(sectionSlug).i18nKey}.description`, {
        defaultValue: '',
      })
    : '';

  useSeoMeta({
    title: mode === 'section' ? `${sectionLabel} — ${guideLabel}` : rootTitle,
    description: mode === 'section'
      ? (sectionDescription || rootDescription)
      : rootDescription,
    canonical:
      mode === 'section' && sectionSlug
        ? buildGuideUrl({ lang, section: sectionSlug, origin: 'https://lodestar.place' })
        : buildGuideUrl({ lang, origin: 'https://lodestar.place' }),
    locale: lang === 'ru' ? 'ru_RU' : 'en_US',
    ogType: 'website',
  });

  useGuideHreflang({ section: sectionSlug ?? undefined });

  useJsonLd(
    JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: mode === 'section' ? `${sectionLabel} — ${guideLabel}` : guideLabel,
      url:
        mode === 'section' && sectionSlug
          ? buildGuideUrl({ lang, section: sectionSlug, origin: 'https://lodestar.place' })
          : buildGuideUrl({ lang, origin: 'https://lodestar.place' }),
      inLanguage: lang,
    }),
    'guide-collection',
  );

  if (mode === 'not-found') {
    return <NotFound lang={lang} />;
  }

  return (
    <div className="mx-auto w-full max-w-4xl px-4 py-8 sm:py-12">
      <nav
        aria-label="breadcrumb"
        className="mb-5 font-mono text-[10px] uppercase tracking-wider text-ink-400"
      >
        <Link to={buildGuideUrl({ lang })} className="hover:text-ink-700">
          {guideLabel}
        </Link>
        {mode === 'section' && (
          <>
            <span aria-hidden="true" className="mx-1.5">/</span>
            <span className="text-ink-700">{sectionLabel}</span>
          </>
        )}
      </nav>

      <header className="mb-10">
        <p className="font-mono text-[10px] uppercase tracking-wider text-ink-500">
          {mode === 'section'
            ? t('guide.sectionTag', { defaultValue: 'Section' })
            : t('guide.rootTag', { defaultValue: 'Documentation' })}
        </p>
        <h1 className="mt-2 font-display text-[clamp(28px,6vw,40px)] font-medium italic tracking-tight text-ink-900">
          {mode === 'section' ? sectionLabel : guideLabel}
        </h1>
        <p className="mt-3 text-base text-ink-700 sm:text-lg">
          {mode === 'section' ? sectionDescription : rootDescription}
        </p>
      </header>

      {mode === 'root' ? <SectionsGrid lang={lang} /> : null}
      {mode === 'section' && sectionSlug ? (
        <ArticlesList lang={lang} section={sectionSlug} />
      ) : null}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────── */

function SectionsGrid({ lang }: { lang: LangCode }) {
  const { t } = useTranslation();
  return (
    <ul className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      {SECTIONS.map((s) => {
        const articles = articlesForSection(s.slug);
        const label = t(`guide.section.${s.i18nKey}.label`, { defaultValue: s.i18nKey });
        const description = t(`guide.section.${s.i18nKey}.description`, { defaultValue: '' });
        return (
          <li key={s.slug}>
            <Link
              to={buildGuideUrl({ lang, section: s.slug })}
              className="flex h-full flex-col rounded-lg border border-ink-100 bg-paper-2/40 px-5 py-4 transition-colors hover:bg-paper-2"
            >
              <span className="flex items-center gap-3">
                <span aria-hidden="true" className="font-mono text-lg text-terra-600">
                  {s.icon}
                </span>
                <span className="font-display text-lg font-medium italic text-ink-900">
                  {label}
                </span>
              </span>
              <span className="mt-2 text-sm text-ink-700">{description}</span>
              <span className="mt-3 font-mono text-[10px] uppercase tracking-wider text-ink-400">
                {t('guide.articleCount', {
                  count: articles.length,
                  defaultValue: '{{count}} articles',
                })}
              </span>
            </Link>
          </li>
        );
      })}
    </ul>
  );
}

function ArticlesList({ lang, section }: { lang: LangCode; section: SectionSlug }) {
  const { t } = useTranslation();
  const articles = articlesForSection(section);
  if (articles.length === 0) {
    return (
      <p className="rounded-md border border-dashed border-ink-100 bg-paper-2/40 px-5 py-6 text-sm text-ink-500">
        {t('guide.emptySection', { defaultValue: 'Articles for this section are in progress.' })}
      </p>
    );
  }
  return (
    <ul className="space-y-3">
      {articles.map((a) => {
        const titleKey = `guide.article.${a.slug}.title`;
        const summaryKey = `guide.article.${a.slug}.summary`;
        return (
          <li key={a.slug}>
            <Link
              to={buildGuideUrl({ lang, section, article: a.slug })}
              className="flex flex-col rounded-md border border-ink-100 bg-paper-2/40 px-5 py-4 transition-colors hover:bg-paper-2"
            >
              <span className="font-display text-base font-medium italic text-ink-900 sm:text-lg">
                {t(titleKey, { defaultValue: a.slug })}
              </span>
              <span className="mt-1 text-sm text-ink-700">
                {t(summaryKey, { defaultValue: '' })}
              </span>
            </Link>
          </li>
        );
      })}
    </ul>
  );
}

function NotFound({ lang }: { lang: LangCode }) {
  const { t } = useTranslation();
  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col items-center px-4 py-20 text-center">
      <h1 className="font-display text-3xl font-medium italic tracking-tight text-ink-900">
        {t('guide.notFound.title', { defaultValue: 'Page not found' })}
      </h1>
      <p className="mt-3 text-sm text-ink-500">
        {t('guide.notFound.subtitle', { defaultValue: 'This page does not exist in the Field Guide.' })}
      </p>
      <Link
        to={buildGuideUrl({ lang })}
        className="mt-6 inline-flex h-11 items-center rounded-md bg-terra-600 px-5 text-sm font-medium text-paper hover:bg-terra-700"
      >
        {t('guide.notFound.cta', { defaultValue: 'Back to Field Guide' })}
      </Link>
    </div>
  );
}
